import { useMemo, useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useReferralCode } from "@/features/referrals/useReferralCode";
import { getJobShareUrl } from "../utils/getJobShareUrl";

/**
 * Share button for a job post: native share sheet on mobile builds,
 * copy-link dialog on web. Styling comes from the caller via className so
 * it can sit in different action rows.
 */
export function JobShareButton({
  jobId,
  jobTitle,
  facilityWithType,
  location,
  className,
}: {
  jobId: string;
  jobTitle?: string | null;
  facilityWithType?: string;
  location?: string;
  className?: string;
}) {
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const referralCode = useReferralCode();
  const jobShareUrl = useMemo(
    () => getJobShareUrl(jobId, referralCode),
    [jobId, referralCode],
  );

  const handleShare = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const shareTitle = jobTitle || "Job Opportunity";
        const shareText = `Check out this job: ${shareTitle}${
          facilityWithType ? ` at ${facilityWithType}` : ""
        }${location ? ` in ${location}` : ""}. ${jobShareUrl}`;

        await Share.share({
          title: shareTitle,
          text: shareText,
          dialogTitle: "Share this job",
        });
      } else {
        setIsShareDialogOpen(true);
      }
    } catch (error) {
      console.error("Error sharing job:", error);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(jobShareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Error copying link:", error);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleShare}
        aria-label="Share job"
        className={className}
      >
        <Share2 className="h-4 w-4" />
      </Button>

      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="[&.dialog-content]:max-w-[min(28rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] rounded-2xl border-slate-200 bg-white text-slate-900 sm:max-w-md sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle>Share this job</DialogTitle>
            <DialogDescription className="text-slate-600">
              Copy the link below to share this job opportunity
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 mt-4">
            <div className="grid min-w-0 flex-1 gap-2">
              <div className="flex items-center">
                <Input
                  className="text-sm text-slate-700 truncate flex-1"
                  aria-label="Job share link"
                  value={jobShareUrl}
                  readOnly
                />
              </div>
            </div>
            <Button
              onClick={handleCopyLink}
              className="shrink-0 px-3"
              variant="outline"
            >
              {isCopied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              <span className="sr-only">Copy</span>
            </Button>
          </div>
          <div className="mt-4">
            <Button
              onClick={() => setIsShareDialogOpen(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
